import { LoanPipelineSecurityGroupSource } from './subCompanyPipelineAccessApi.constants';

/**
 * Broker-side pipeline-access client (DEV-4697). The matrix is composed in redux from THREE real,
 * shipped contracts — no single bespoke endpoint:
 *
 *  - Exposed pipelines (the matrix ROWS + their effective channel scope): SkynetAPI BFF
 *    `GET /api/admin-panel/sub-companies/{subCompanyId}/exposed-pipelines`
 *    → { pipelines: [{ pipelineId, pipelineName, scopeType, effectiveChannelIds, defaultTemplateNames }] }
 *    (fail-soft — pipelineName degrades to null on a Loans outage; empty list, never a 500).
 *
 *  - Per-pipeline attachments (the matrix CELLS — provenance/isRevoked/isLocked + role id):
 *    Skynet.Loans `GET api/loanPipelines/{id}/securityGroups`
 *    → [{ securityGroupId, source, isRevoked, isLocked, securityGroupName }].
 *
 * The role COLUMNS come from the existing sub-company allowed-roles source (subCompaniesApi), and
 * the channel names from the broker-safe ceiling-derivation source — neither is called here.
 *
 * The exposed-pipelines GET rides the SkynetAPI gateway; the securityGroups reads/writes ride the
 * Loans gateway. All are scoped server-side (the broker only ever sees its own subtree).
 */
export class SubCompanyPipelineAccessApi {
  #skynetHttp;

  #loansHttp;

  constructor(skynetHttp, loansHttp) {
    this.#skynetHttp = skynetHttp;
    this.#loansHttp = loansHttp;
  }

  // Matrix rows. Degradable broker read: skip the global forbidden-status page-reload interceptor
  // so a failed fetch surfaces the in-tab error+retry state instead of reloading the whole app.
  async getExposedPipelines({ subCompanyId }) {
    return this.#skynetHttp.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/exposed-pipelines`,
      { skipResponseInterceptor: { forbiddenError: true } },
    );
  }

  // Matrix cells for ONE exposed pipeline.
  async getPipelineSecurityGroups({ pipelineId }) {
    return this.#loansHttp.get(`/api/loanPipelines/${pipelineId}/securityGroups`);
  }

  // Turn a role ON for a pipeline. ONE upsert covers both broker gestures the backend supports:
  //   - a role never granted here  -> creates a SubCompanyManaged grant (gated by the exposure ceiling);
  //   - a broker-hidden seeded default -> the handler detects the existing TpoSeeded row, keeps it
  //     TpoSeeded, and clears its IsRevoked tombstone (the sanctioned un-hide/restore path).
  // The broker must always send SubCompanyManaged with isLocked:false — TpoSeeded/isLocked writes 403.
  async grantPipelineSecurityGroup({ pipelineId, securityGroupId }) {
    return this.#loansHttp.post(`/api/loanPipelines/${pipelineId}/securityGroups`, {
      securityGroupId,
      source: LoanPipelineSecurityGroupSource.SUB_COMPANY_MANAGED,
      isLocked: false,
    });
  }

  // Turn a role OFF. The backend picks tombstone-vs-delete by the stored provenance: a seeded
  // default is upserted IsRevoked=true (a tombstone the reconcile respects), a broker-added grant
  // is hard-deleted. The UI sends the same DELETE regardless.
  async revokePipelineSecurityGroup({ pipelineId, securityGroupId }) {
    return this.#loansHttp.delete(
      `/api/loanPipelines/${pipelineId}/securityGroups/${securityGroupId}`,
    );
  }

  // Reset ONE pipeline to its seeded defaults: drops the broker's own grants and un-hides every
  // hidden default for the caller's sub-company. A broker omits subCompanyId (server defaults to
  // the authenticated sub-company).
  async resetPipelineSecurityGroups({ pipelineId }) {
    return this.#loansHttp.post(`/api/loanPipelines/${pipelineId}/securityGroups/reset`, {});
  }
}
